import { ReactNode, useEffect, useRef, useState, useCallback } from 'react';
import Alert from 'atol-atoms-2/Alert'
import Button from 'atol-atoms-2/Button';
import IconStatus from 'atol-atoms-2/IconStatus';
import { useUserStore } from '@/stores/user';
import styles from './style.module.scss';
import { ApiRequestInterceptors } from '@/app/api';
import {
    keycloak,
    keycloakInitOptions,
    AUTH_TOKEN_INTERCEPTOR_NAME,
    LOGOUT_SYNC_KEY,
    TOKEN_AUTO_UPDATE_INTERCEPTOR_NAME,
    KEYCLOAK_UPDATE_TOKEN_TIMEOUT_SEC,
    KEYCLOAK_UPDATE_TOKEN_CHECK,
} from './constants';
import { KeycloakContext } from './context';

export const KeycloakProvider = ({ children }: { children: ReactNode }) => {
    const initializationStartedRef = useRef(false);
    const [keycloakInitialized, setKeycloakInitialized] = useState(false);
    const [initError, setInitError] = useState(false);
    const setAuthData = useUserStore((store) => store.setAuthData);

    const initKeycloak = async () => {
        if (initializationStartedRef.current || keycloakInitialized) return;
        initializationStartedRef.current = true;

        try {
            const authenticated = await keycloak.init(keycloakInitOptions);

            if (authenticated) {
                if (!keycloak.token || !keycloak.tokenParsed) {
                    throw new Error('Токен аутентификации keycloak не найден');
                }

                setAuthData(keycloak.token, keycloak.tokenParsed);

                if (!ApiRequestInterceptors.has(TOKEN_AUTO_UPDATE_INTERCEPTOR_NAME)) {
                    ApiRequestInterceptors.set(TOKEN_AUTO_UPDATE_INTERCEPTOR_NAME, async (params) => {
                        try {
                            if (keycloak.isTokenExpired(KEYCLOAK_UPDATE_TOKEN_TIMEOUT_SEC / 2)) await keycloak.updateToken(KEYCLOAK_UPDATE_TOKEN_TIMEOUT_SEC / 2);
                            return params;
                        } catch {
                            await keycloak.login();
                            return params;
                        }
                    });
                }

                if (!ApiRequestInterceptors.has(AUTH_TOKEN_INTERCEPTOR_NAME)) {
                    ApiRequestInterceptors.set(AUTH_TOKEN_INTERCEPTOR_NAME, async (params) => {
                        if (!(params.options.headers instanceof Headers)) params.options.headers = new Headers(params.options.headers)

                        if (!params.options.headers.has('Content-Type')) params.options.headers.append('Content-Type', 'application/json')
                        params.options.headers.append('Authorization', `Bearer ${keycloak.token}`)

                        return params;
                    });
                }
                localStorage.removeItem(LOGOUT_SYNC_KEY);
                setKeycloakInitialized(true);
            } else {
                await keycloak.login();
            }
        } catch (error) {
            console.error('Keycloak не инициализирован, перенаправление на login', error);

            try {
                keycloak.clearToken()
                await keycloak.login();
            } catch (loginError) {
                console.error('Ошибка перенаправления на login', loginError);
                setInitError(true);
            } finally {
                initializationStartedRef.current = false;
            }
        }
    };

    useEffect(() => {
        if (!keycloakInitialized) return;

        const interval = setInterval(async () => {
            try {
                await keycloak.updateToken(KEYCLOAK_UPDATE_TOKEN_TIMEOUT_SEC);
            } catch (e) {
                console.error(e)
                await keycloak.login();
            }
        }, KEYCLOAK_UPDATE_TOKEN_CHECK);

        return () => clearInterval(interval);
    }, [keycloakInitialized]);

    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === LOGOUT_SYNC_KEY && e.newValue && keycloakInitialized) {
                keycloak.logout({ redirectUri: window.location.href });
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [keycloakInitialized]);

    const logout = useCallback(async () => {
        localStorage.setItem(LOGOUT_SYNC_KEY, Date.now().toString());
        return keycloak.logout({ redirectUri: window.location.href });
    }, []);

    const updateToken = useCallback(() => keycloak.updateToken(), [])

    useEffect(() => {
        void initKeycloak();
    }, []);

    if (initError) {
        return (
            <div className={styles.loader}>
                <Alert
                    title="Ошибка авторизации"
                    status="negative"
                    description="Проблемы на сервере, попробуйте обновить страницу и повторить попытку"
                    button={
                        <Button buttonType="tertiary" size="s" onClick={() => window.location.reload()}>
                            Обновить страницу
                        </Button>
                    }
                />
            </div>
        );
    }

    if (!keycloakInitialized) {
        return (
            <div className={styles.loader}>
                <IconStatus status="loading" size={58} />
            </div>
        );
    }

    const context = {
        get token() {
            return keycloak.token
        },
        get tokenParsed() {
            return keycloak.tokenParsed
        },
        logout,
        updateToken,
    }

    return <KeycloakContext.Provider value={context}>
        {children}
    </KeycloakContext.Provider>
};
